import { useMemo, useState } from "react";

import { TextFormat as TextFormatIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { postItemTypeOptionBulkRecase } from "@rubi/api-generated/clientRs/sdk.gen";
import type { CasingStyle } from "@rubi/api-generated/clientRs/types.gen";
import { toast } from "@rubi/ui";

import { CASING_LABELS, CASING_OPTIONS } from "./casingOptions";

// A dropdown list shown in the picker. `disabledReason`, when set, means the list can't be
// bulk-edited (legacy v1 or read-only shared) - it's shown greyed out with a tooltip.
export interface BulkRecaseTarget {
  id: string;
  name: string;
  itemCount: number;
  disabledReason?: string;
}

interface BulkRecaseDropdownsModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  targets: BulkRecaseTarget[];
  onSuccess: () => void;
}

const BulkRecaseDropdownsModal = ({
  open,
  onClose,
  orgId,
  targets,
  onSuccess,
}: BulkRecaseDropdownsModalProps) => {
  const [casing, setCasing] = useState<CasingStyle>("titleCase");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const selectableTargets = useMemo(
    () => targets.filter((t) => !t.disabledReason),
    [targets],
  );

  const allSelected =
    selectableTargets.length > 0 && selected.size === selectableTargets.length;
  const someSelected = selected.size > 0 && !allSelected;

  const selectedItemCount = useMemo(
    () =>
      selectableTargets
        .filter((t) => selected.has(t.id))
        .reduce((sum, t) => sum + t.itemCount, 0),
    [selectableTargets, selected],
  );

  const resetAndClose = () => {
    if (applying) return;
    setSelected(new Set());
    setConfirmOpen(false);
    setCasing("titleCase");
    onClose();
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) =>
      prev.size === selectableTargets.length
        ? new Set()
        : new Set(selectableTargets.map((t) => t.id)),
    );
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const response = await postItemTypeOptionBulkRecase({
        path: { org_id: orgId },
        body: {
          casing,
          item_type_option_ids: Array.from(selected),
        },
      });

      const { success_count = 0, failed_count = 0 } = response.data ?? {};

      if (success_count > 0) {
        toast.success(
          `Applied ${CASING_LABELS[casing]} to ${success_count} list${
            success_count === 1 ? "" : "s"
          }`,
        );
      }
      if (failed_count > 0) {
        const firstError = response.data?.results?.find(
          (r) => r.status === "failed",
        )?.error;
        toast.error(
          `${failed_count} list${failed_count === 1 ? "" : "s"} could not be updated${
            firstError ? `: ${firstError}` : ""
          }`,
        );
      }

      setApplying(false);
      setConfirmOpen(false);
      setSelected(new Set());
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Failed to bulk re-case dropdowns:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to apply bulk edit";
      toast.error(message);
      setApplying(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Edit Dropdowns</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Apply a casing style to every item label across the lists you
            select. Only labels change — values, codes, and translations are
            left untouched. Changes are saved immediately.
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="bulk-recase-action-label">Action</InputLabel>
            <Select
              labelId="bulk-recase-action-label"
              label="Action"
              value={casing}
              onChange={(e) => setCasing(e.target.value as CasingStyle)}
            >
              {CASING_OPTIONS.map((option) => (
                <MenuItem key={option.style} value={option.style}>
                  {option.label} — e.g. &quot;{option.example}&quot;
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleSelectAll}
                  disabled={selectableTargets.length === 0}
                />
              }
              label="Apply to lists"
            />
            <Typography variant="caption" color="text.secondary">
              {selected.size} selected
            </Typography>
          </Box>

          <List
            dense
            sx={{
              maxHeight: 320,
              overflow: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {targets.map((target) => {
              const disabled = Boolean(target.disabledReason);
              const row = (
                <ListItem key={target.id} disablePadding>
                  <ListItemButton
                    dense
                    disabled={disabled}
                    onClick={() => toggle(target.id)}
                  >
                    <Checkbox
                      edge="start"
                      checked={selected.has(target.id)}
                      disabled={disabled}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText
                      primary={target.name || "(unnamed list)"}
                      secondary={
                        disabled
                          ? target.disabledReason
                          : `${target.itemCount} item${
                              target.itemCount === 1 ? "" : "s"
                            }`
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );

              return disabled ? (
                <Tooltip
                  key={target.id}
                  title={target.disabledReason}
                  placement="left"
                >
                  <span>{row}</span>
                </Tooltip>
              ) : (
                row
              );
            })}
            {targets.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="text.secondary">
                      No dropdown lists found
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAndClose}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<TextFormatIcon />}
            disabled={selected.size === 0}
            onClick={() => setConfirmOpen(true)}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => !applying && setConfirmOpen(false)}
      >
        <DialogTitle>Apply {CASING_LABELS[casing]}?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This updates labels immediately and cannot be undone in one click
            (you can re-run with a different style).
          </Alert>
          <Typography variant="body2">
            Apply <strong>{CASING_LABELS[casing]}</strong> to{" "}
            <strong>
              {selected.size} list{selected.size === 1 ? "" : "s"}
            </strong>{" "}
            ({selectedItemCount} item{selectedItemCount === 1 ? "" : "s"}).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={applying}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={applying}
            startIcon={
              applying ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <TextFormatIcon />
              )
            }
          >
            {applying ? "Applying..." : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkRecaseDropdownsModal;
